/**
 * Home.jsx — personadraft.com (site handoff: "Sound like yourself, everywhere").
 *
 * A behavioral engine with cross-surface consistency, told in four moves —
 * Mapped → Pinpointed → Learned → Compiled — for four readers at once
 * (everyday writer, technical skeptic, investor, business buyer).
 *
 * Order: hero (the product loop) → four moves → control → every surface →
 * ships prompts → trust → Teams band → final CTA. The Teams band stays below
 * the consumer story. Every live / coming-soon string reads siteConfig.js.
 */
import { useEffect, useRef, useState } from 'react';
import { SiteNav, SiteFooter, FinalCta, SiteLink, PMark, CHROME_STORE_URL, SUPPORT_EMAIL, spa } from '../SiteShell.jsx';
import { LightStudioWindow } from '../StudioWindow.jsx';
import { useFit, useStepLoop, useReducedMotion, useHashScroll, StepControls } from '../SiteMotion.jsx';
import { AUTOPLAY, SURFACES, isLive, HERO_LIVE, HERO_SOON, WAITLIST_PATH } from '../siteConfig.js';
import { TYPED, ME_VARIANTS, OTHER_VOICES } from '../content/homeDrafts.js';

const hide = (on) => (on ? '' : ' s-hide');

/* ── Hero: the product loop ─────────────────────────────────────────────── */

const HERO_MS = [3400, 2600, 4400, 4400, 6800, 3800];
const HERO_CAPS = ['01 · YOU TYPE', '02 · ONE CLICK', '03 · IN YOUR VOICE', '04 · ONE TAP WARMER', '05 · THE WINDOW', '06 · YOU PRESS SEND'];
// TYPED and the email drafts (hero, Control, the Gmail tab) are engine output:
// content/homeDrafts.js. The other surface tabs are still hand-written.
const ME = ME_VARIANTS.me;
const WARM = ME_VARIANTS.warm;
// The thread is there so the hero is never empty before the window opens.
const THREAD = [
  { a: 'G', n: 'Gabbi Noa', tm: '4:12 PM', t: 'Engineering wants to push the launch from Wednesday to Friday. Can you confirm by EOD?' },
  { a: 'M', n: 'You', tm: '4:20 PM', t: 'Friday works if the vendor confirms delivery. I’m checking with them now and will come back to you.', me: true },
  { a: 'S', n: 'Sam Ortiz', tm: '4:30 PM', t: 'Vendor just called. They can’t deliver until next week, and they won’t commit to a date yet.' },
  { a: 'G', n: 'Gabbi Noa', tm: '4:41 PM', t: 'Press list goes out at 9 tomorrow, so I need your call tonight. Do we hold or move?' },
];

function ChatMsg({ m }) {
  return (
    <div className="s-cm">
      <span className={`s-av${m.me ? ' me' : ''}`} aria-hidden="true">{m.a}</span>
      <div className="s-cm-b">
        <div className="s-cm-h"><b>{m.n}</b> <time>{m.tm}</time></div>
        <div className="s-cm-t">{m.t}</div>
      </div>
    </div>
  );
}

function HeroLoop() {
  const { step: s, playing, toggle, go } = useStepLoop(HERO_MS, 4);
  const [ref, sc] = useFit(600);
  const lineOn = s === 2 || s === 3;
  const warm = s === 3;
  const dot = s === 1 ? '#3D7DE0' : s === 4 ? '#F2B01E' : '#2FB46B';
  return (
    <div className="s-hero2-vis" ref={ref}>
      <div className={`s-cv hx${s >= 2 ? ' grown' : ''}`} style={{ transform: `scale(${sc})` }}>
        <div className="hx-thread">
          <div className="hx-thread-in">
            <div className="hx-title"><b>Friday launch</b><span className="s-count">4 MESSAGES</span></div>
            {THREAD.map((m) => <ChatMsg key={m.tm} m={m} />)}
          </div>
        </div>

        <div className="hx-comp">
          <div className="hx-to">To<span className="hx-chip">Gabbi Noa</span><span className="hx-re">Re: Friday launch</span></div>
          <div className="hx-box">
            <div className="hx-text">{s < 2 ? TYPED : s === 2 ? ME : WARM}</div>
            <div className={`s-rw hx-rw${hide(s === 1)}`}><i />REWRITING IN ME</div>
            <span className="hx-send">Send</span>
            <span className="s-mark hx-mark" style={{ boxShadow: s === 0 ? '0 4px 12px rgba(15,20,32,.28), 0 0 0 5px rgba(242,140,30,.22)' : undefined }}>
              <PMark size={16} white dot={false} />
              <i style={{ background: dot, opacity: s >= 1 && s <= 4 ? 1 : 0 }} />
            </span>
          </div>
        </div>

        <div className={`hx-tip${hide(s === 0)}`}>
          <b>Click</b> · sound like me <span className="k">⌘ .</span><br />
          <span className="d">Double-click · open PersonaDraft</span> <span className="k">⌘⇧ .</span>
        </div>

        <div className={`s-line hx-line${hide(lineOn)}`} style={{ transform: lineOn ? 'none' : 'translateY(6px)' }}>
          <PMark size={16} />
          <span className="rw">Rewritten in <b>{warm ? 'Me, warmer' : 'Me'}</b></span>
          <span className="tuned">TUNED FOR GMAIL</span>
          <span className="sep" />
          <span className={`act${warm ? ' on' : ''}`}>Warmer</span>
          <span className="act">Firmer</span>
          <span className="undo">↶ Undo</span>
          <span className="hx-more">⋯</span>
          <span className="hx-close">✕</span>
        </div>

        <div className={`hx-win${hide(s === 4)}`} style={{ transform: s === 4 ? 'none' : 'translateY(8px) scale(.985)' }}>
          <LightStudioWindow label="DRAFT 2 / 2 · ME, WARMER" metaLead="A reply to Gabbi" metaFraming="tell them it’s moved" metaRead="read 4 messages"
            draft={WARM} voice="me" variant="warm" />
        </div>

        <div className={`s-line hx-done${hide(s === 5)}`}>
          <span className="ok" />
          <span className="rw">Inserted in <b>Me, warmer</b> · you press Send.</span>
          <span className="u">↶ Undo</span>
          <span className="x">✕</span>
        </div>

        <StepControls className="hx-ctrl" step={s} playing={playing} onToggle={toggle} onGo={go} caps={HERO_CAPS} label="the product demo" />
      </div>
    </div>
  );
}

/* ── Four moves ─────────────────────────────────────────────────────────── */

const MOVES = [
  ['01', 'MAPPED', 'Your voice, mapped.', 'Eight measurable behavioral axes, grounded in psychology. Not adjectives.'],
  ['02', 'PINPOINTED', 'Pinpointed to you.', 'Your voice becomes one exact coordinate. Nobody else stands on it.'],
  ['03', 'LEARNED', 'It learns, everywhere you write.', 'With your consent, everything you write sharpens that point, kept in one place.'],
  ['04', 'COMPILED', 'Consistent, on every surface.', 'Every draft is written from your coordinate, so it always sounds like you.'],
];
// Layer opacities per move: axes · other voices · interval band · You · this draft.
const MOVE_OP = [
  { axes: 1, voices: 0.75, band: 0, you: 0, comp: 0 },
  { axes: 0.6, voices: 0.22, band: 0, you: 1, comp: 0 },
  { axes: 0.6, voices: 0.1, band: 1, you: 1, comp: 0 },
  { axes: 0.6, voices: 0.1, band: 0, you: 0.3, comp: 1 },
];
const AXES = ['Assertive', 'Warm', 'Detail', 'Urgency', 'Expressive', 'Tact', 'Length', 'Formality'];
const MOVE_X = [50, 150, 250, 350, 450, 550, 650, 750];
const YOU_Y = [136, 164, 190, 118, 172, 96, 140, 200];
const COMP_Y = [130, 150, 190, 98, 172, 126, 100, 200];
const BAND_Y = [120, 148, 174, 102, 156, 80, 124, 184];
const line = (ys) => ys.map((y, i) => `${i ? 'L' : 'M'}${MOVE_X[i]} ${y}`).join('');

function MovesChart({ op }) {
  return (
    <svg viewBox="0 0 800 280" role="img" aria-label="Parallel-coordinates chart: eight behavioral axes, other voices, your voice as one line, and this draft compiled from it">
      <g className="s-fade5" style={{ opacity: op.axes }}>
        <path d="M50 110H750M50 160H750M50 210H750" stroke="rgba(15,20,32,.08)" strokeDasharray="3 4" />
        <path d="M50 60V260M150 60V260M250 60V260M350 60V260M450 60V260M550 60V260M650 60V260M750 60V260" stroke="rgba(15,20,32,.24)" />
        <g fontFamily="Manrope,system-ui,sans-serif" fontSize="11.5" fontWeight="800" fill="#0F1420" textAnchor="middle">
          {AXES.map((a, i) => <text key={a} x={MOVE_X[i]} y="40">{a}</text>)}
        </g>
      </g>
      <g className="s-fade5" style={{ opacity: op.voices }} fill="none" strokeWidth="1.3" strokeLinejoin="round">
        <path d="M50 80L150 200L250 160L350 140L450 190L550 90L650 100L750 180" stroke="#E2455A" />
        <path d="M50 190L150 90L250 170L350 110L450 140L550 190L650 170L750 190" stroke="#3D7DE0" />
        <path d="M50 140L150 190L250 80L350 120L450 210L550 140L650 160L750 230" stroke="#1BB8C4" />
        <path d="M50 170L150 130L250 210L350 180L450 110L550 110L650 90L750 90" stroke="#9B5DE0" />
        <path d="M50 120L150 140L250 200L350 190L450 80L550 120L650 180L750 160" stroke="#D8519E" />
        <path d="M50 160L150 150L250 160L350 140L450 160L550 180L650 190L750 220" stroke="#5C6170" strokeWidth="1.5" strokeDasharray="5 4" />
      </g>
      <g className="s-fade5" style={{ opacity: op.band }}>
        <g fill="rgba(15,20,32,.12)">{BAND_Y.map((y, i) => <rect key={i} x={MOVE_X[i] - 6} y={y} width="12" height="32" rx="6" />)}</g>
        <g fill="none" stroke="rgba(15,20,32,.16)" strokeWidth=".9">
          <path d="M50 128L150 172L250 182L350 126L450 164L550 104L650 132L750 208" />
          <path d="M50 144L150 156L250 198L350 110L450 180L550 88L650 148L750 192" />
          <path d="M50 132L150 168L250 186L350 122L450 168L550 100L650 136L750 204" />
        </g>
      </g>
      <g className="s-fade5" style={{ opacity: op.you }}>
        <path d={line(YOU_Y)} fill="none" stroke="#0F1420" strokeWidth="2.2" strokeLinejoin="round" />
        <g fill="#0F1420" stroke="#fff" strokeWidth="2">{YOU_Y.map((y, i) => <circle key={i} cx={MOVE_X[i]} cy={y} r="4.5" />)}</g>
        <text x="764" y="204" fontFamily="Manrope,system-ui,sans-serif" fontSize="12" fontWeight="800" fill="#0F1420">You</text>
      </g>
      <g className="s-fade5" style={{ opacity: op.comp }}>
        <path d={line(COMP_Y)} fill="none" stroke="#F28C1E" strokeWidth="2.4" strokeLinejoin="round" />
        <g fill="#F28C1E" stroke="#fff" strokeWidth="2">{COMP_Y.map((y, i) => <circle key={i} cx={MOVE_X[i]} cy={y} r="4.5" />)}</g>
        <text x="764" y="104" fontFamily="Manrope,system-ui,sans-serif" fontSize="12" fontWeight="800" fill="#B8621C">This draft</text>
      </g>
    </svg>
  );
}

function FourMoves() {
  const reduced = useReducedMotion();
  const [move, setMove] = useState(0);
  const [auto, setAuto] = useState(true);
  useEffect(() => { if (reduced || !AUTOPLAY) setAuto(false); }, [reduced]);
  useEffect(() => {
    if (!auto) return undefined;
    const t = setInterval(() => setMove((m) => (m + 1) % MOVES.length), 3800);
    return () => clearInterval(t);
  }, [auto]);
  // Hover or click picks a move and stops the cycle.
  const pick = (i) => { setMove(i); setAuto(false); };
  return (
    <section className="s-moves s-top" id="moves">
      <div className="s-w2">
        <div className="s-moves-head">
          <h2 className="s-eyebrow">HOW PERSONADRAFT WORKS · FOUR MOVES</h2>
          <a className="s-arrow" href="/how-it-works#act2" onClick={spa('/how-it-works#act2')}>See the engine, step by step →</a>
        </div>
        <div className="s-moves-chart"><MovesChart op={MOVE_OP[move]} /></div>
        <div className="s-moves-cols">
          {MOVES.map(([n, name, head, sub], i) => (
            <div key={n} className={`s-move${i === move ? ' on' : ''}`} onMouseEnter={() => pick(i)} onClick={() => pick(i)}>
              <div className="s-move-k">{n} · {name}</div>
              <h3>{head}</h3>
              <p>{sub}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Control: me warmer / me firmer, and the seven other voices ─────────── */

const VOICE_NAME = { me: 'Me', direct: 'Direct', bold: 'Bold', gentle: 'Gentle', precise: 'Precise', steady: 'Steady', casual: 'Casual', visionary: 'Visionary' };
const VARIANT_WORD = { me: '', warm: 'warmer', firm: 'firmer', fast: 'faster' };

function Control() {
  const [st, setSt] = useState({ voice: 'me', variant: 'warm', n: 2 });
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const [ref, sc] = useFit(480);
  useEffect(() => () => clearTimeout(timer.current), []);
  // Every change is a new draft: re-draft for 420ms, then swap it in.
  const redraft = (patch) => {
    setBusy(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { setSt((x) => ({ ...x, ...patch, n: x.n + 1 })); setBusy(false); }, 420);
  };
  const isMe = st.voice === 'me';
  const name = isMe ? (st.variant === 'me' ? 'Me' : `Me, ${VARIANT_WORD[st.variant]}`) : VOICE_NAME[st.voice];
  const warmOn = isMe && st.variant === 'warm';
  const firmOn = isMe && st.variant === 'firm';
  return (
    <section className="s-ctrl s-top">
      <div className="s-w2">
        <div className="s-ctrl-copy">
          <div className="s-eyebrow">CONTROL, AS WELL AS ACCURACY</div>
          <h2 className="s-h2v">Warmer. Firmer. Or someone else, for one draft.</h2>
          <p className="s-sub17">One tap nudges your own voice. Seven other voices are there when a moment needs one, and your Me stays exactly where it is.</p>
          <div className="s-line s-ctrl-line">
            <PMark size={16} />
            <span className="rw">Rewritten in <b>{name}</b></span>
            <span className="sep" />
            <button type="button" className={`act${warmOn ? ' on' : ''}`} aria-pressed={warmOn} onClick={() => redraft({ voice: 'me', variant: warmOn ? 'me' : 'warm' })}>Warmer</button>
            <button type="button" className={`act${firmOn ? ' on' : ''}`} aria-pressed={firmOn} onClick={() => redraft({ voice: 'me', variant: firmOn ? 'me' : 'firm' })}>Firmer</button>
            <button type="button" className="undo" onClick={() => redraft({ voice: 'me', variant: 'me' })}>↶ Undo</button>
          </div>
          <div className="s-cap">THE LINE · ONE TAP, ON EVERY SURFACE · TRY IT</div>
          <div className="s-bul">
            <div><i style={{ background: '#0F1420' }} /><span><b>Me, warmer. Me, firmer.</b> Your voice, nudged. Never replaced.</span></div>
            <div><i style={{ background: '#3D7DE0' }} /><span><b>Seven other voices</b> for the drafts that need one. Me doesn’t move.</span></div>
            <div><i style={{ background: '#F28C1E' }} /><span><b>Every change is a new draft.</b> ‹ › walks them back.</span></div>
          </div>
        </div>
        <div className="s-ctrl-vis" ref={ref}>
          <div className="s-cv ctl-cv" style={{ transform: `scale(${sc})` }}>
            <LightStudioWindow label={`DRAFT ${st.n} / ${st.n} · ${name.toUpperCase()}`} metaLead="A reply to Gabbi" metaFraming="tell them it’s moved" metaRead="read 3 messages"
              draft={isMe ? ME_VARIANTS[st.variant] : OTHER_VOICES[st.voice]} voice={st.voice} variant={st.variant} busy={busy}
              onVoice={(id) => redraft({ voice: id })} onVariant={(id) => redraft({ voice: 'me', variant: id })} />
            <div className="s-cap">THE WINDOW · CLICK A VOICE OR A CHIP</div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── One product, every surface ─────────────────────────────────────────── */

const SURF = {
  gmail: { title: 'Re: Friday launch', msgs: [{ a: 'G', n: 'Gabbi Noa', tm: '4:12 PM', t: 'Engineering wants to push the launch to Friday. Can you confirm by EOD?' }],
    typed: TYPED, meta: 'A reply to Gabbi', framing: 'tell them it’s moved', read: 'read 3 messages',
    surface: '', lenIdx: 1, lenBy: 'saved', formIdx: 2, formBy: 'saved',
    draft: ME_VARIANTS.me },
  linkedin: { title: 'Priya Shah · post', msgs: [{ a: 'P', n: 'Priya Shah', tm: '2h', t: 'We shipped the new onboarding today. Six weeks, three rewrites, one very patient design team.' }],
    typed: 'congrats!! looks great. how did u ship so fast', meta: 'A LinkedIn comment', framing: 'congratulate and ask', read: 'read the post',
    surface: 'LINKEDIN', lenIdx: 1, lenBy: 'saved', formIdx: 1, formBy: 'surface',
    draft: "Congrats, Priya — the new onboarding is a real step up. Six weeks is fast; I'm curious what you cut to ship it." },
  slack: { title: '# launch', msgs: [{ a: 'G', n: 'Gabbi Noa', tm: '4:12 PM', t: 'Are we still good for Friday?' }, { a: 'S', n: 'Sam Ortiz', tm: '4:14 PM', t: 'Vendor just called. Not great news.' }],
    typed: "friday's off, vendor fell thru. new date soon", meta: 'A message in #launch', framing: 'tell them it’s moved', read: 'read 4 messages',
    surface: 'SLACK', lenIdx: 2, lenBy: 'surface', formIdx: 2, formBy: 'saved',
    draft: "Heads up: Friday's off — the vendor fell through. New date by Tuesday; I'll post the plan here." },
  whatsapp: { title: 'Sam Ortiz', msgs: [{ a: 'S', n: 'Sam Ortiz', tm: '9:41', t: 'so are we launching friday or not' }],
    typed: 'no. vendor fell thru. ugh', meta: 'A reply to Sam', framing: 'tell them it’s moved', read: 'read 6 messages',
    surface: 'WHATSAPP', lenIdx: 2, lenBy: 'surface', formIdx: 2, formBy: 'saved',
    draft: "Not Friday — the vendor fell through. New date by Tuesday; I'll send it the minute I have it." },
};

function EverySurface() {
  const [key, setKey] = useState('gmail');
  const [ref, sc] = useFit(1000);
  const cur = SURF[key];
  const name = SURFACES.find((x) => x.key === key).name;
  const live = isLive(key);
  return (
    <section className="s-surf s-top">
      <div className="s-w2">
        <div className="s-surf-head">
          <div className="s-eyebrow">ONE PRODUCT, EVERY SURFACE</div>
          <h2 className="s-h2v">One product. Every surface.</h2>
          <p className="s-sub17">The same mark, the same window, the same you. One voice profile follows you everywhere; each surface only adjusts the register, and tells you when it does.</p>
        </div>
        <div className="s-tabs" role="tablist" aria-label="Surfaces">
          {SURFACES.map((x) => {
            const on = x.key === key;
            return (
              <button key={x.key} type="button" role="tab" id={`s-surf-tab-${x.key}`} aria-selected={on} aria-controls="s-surf-stage"
                className={`s-tab${on ? ' on' : ''}`} onClick={() => setKey(x.key)}>
                {x.name}<small className={isLive(x.key) ? 'live' : undefined}>{isLive(x.key) ? 'LIVE' : 'SOON'}</small>
              </button>
            );
          })}
        </div>
        <div className="s-surf-vis" ref={ref} id="s-surf-stage" role="tabpanel" aria-labelledby={`s-surf-tab-${key}`}>
          <div className="s-cv sx" style={{ transform: `scale(${sc})` }}>
            <div className="sx-bar">
              <span className="sx-lights" aria-hidden="true"><i /><i /><i /></span>
              <b>{name}</b><span className="t">{cur.title}</span>
              <span className={`s-tag ${live ? 'live' : 'soon'}`}>{live ? 'LIVE' : 'COMING SOON'}</span>
            </div>
            <div className="sx-side" aria-hidden="true"><i /><i /><i /><i /></div>
            <div className="sx-msgs">{cur.msgs.map((m) => <ChatMsg key={m.tm} m={{ ...m, me: true }} />)}</div>
            <div className="sx-comp">
              {cur.typed}
              <span className="s-mark sx-mark"><PMark size={16} white dot={false} /><i style={{ background: '#F2B01E' }} /></span>
            </div>
            <div className="sx-win">
              <LightStudioWindow label="DRAFT 1 / 1 · ME" metaLead={cur.meta} metaFraming={cur.framing} metaRead={cur.read} draft={cur.draft}
                voice="me" variant="me" surface={cur.surface} lenIdx={cur.lenIdx} lenBy={cur.lenBy} formIdx={cur.formIdx} formBy={cur.formBy} />
            </div>
          </div>
        </div>
        <div className="s-surf-foot">
          <span>{live ? `Live in ${name} today.` : `${name} is coming soon. Same mark, same window, same you.`}</span>
          {live
            ? <a className="s-ulink" href={CHROME_STORE_URL} target="_blank" rel="noopener noreferrer">Add to Chrome, free.</a>
            : <SiteLink className="s-ulink" href={WAITLIST_PATH}>Join the waitlist →</SiteLink>}
        </div>
      </div>
    </section>
  );
}

/* ── Everyone else ships prompts ────────────────────────────────────────── */

function ShipsPrompts() {
  return (
    <section className="s-ships s-top">
      <svg className="s-ships-bg" viewBox="0 0 1200 560" preserveAspectRatio="xMaxYMid slice" aria-hidden="true">
        <defs><pattern id="pdDots" width="16" height="16" patternUnits="userSpaceOnUse"><circle cx="1" cy="1" r=".7" fill="rgba(15,20,32,.12)" /></pattern></defs>
        <rect width="1200" height="560" fill="url(#pdDots)" />
        <g transform="translate(-44 0)">
          <path d="M440 24h10M440 24v10M1176 24h-10M1176 24v10M440 536h10M440 536v-10M1176 536h-10M1176 536v-10" stroke="rgba(15,20,32,.35)" fill="none" />
          <text x="1160" y="44" textAnchor="end" fontFamily="ui-monospace,Menlo,monospace" fontSize="10" letterSpacing="1.4" fill="rgba(15,20,32,.45)">PARALLEL COORDINATES · 8 AXES</text>
          <path d="M480 100V460M574 100V460M668 100V460M762 100V460M856 100V460M950 100V460M1044 100V460M1138 100V460" stroke="rgba(15,20,32,.24)" />
          <path d="M480 190H1138M480 280H1138M480 370H1138" stroke="rgba(15,20,32,.07)" strokeDasharray="3 4" />
          <g fontFamily="Manrope,system-ui,sans-serif" fontSize="12.5" fontWeight="800" fill="#0F1420" textAnchor="middle">
            {AXES.map((a, i) => <text key={a} x={480 + i * 94} y="80">{a}</text>)}
          </g>
          <g fill="none" strokeWidth="1.2" opacity=".38" strokeLinejoin="round">
            <path d="M480 136L574 352L668 280L762 244L856 334L950 154L1044 172L1138 316" stroke="#E2455A" />
            <path d="M480 172L574 208L668 352L762 352L856 154L950 172L1044 226L1138 244" stroke="#F2762B" />
            <path d="M480 334L574 154L668 298L762 190L856 244L950 334L1044 298L1138 334" stroke="#3D7DE0" />
            <path d="M480 244L574 334L668 136L762 208L856 370L950 244L1044 280L1138 406" stroke="#1BB8C4" />
            <path d="M480 280L574 280L668 190L762 172L856 316L950 280L1044 262L1138 316" stroke="#2FB46B" />
            <path d="M480 298L574 226L668 370L762 316L856 190L950 190L1044 154L1138 154" stroke="#9B5DE0" />
            <path d="M480 208L574 244L668 352L762 334L856 136L950 208L1044 316L1138 280" stroke="#D8519E" />
          </g>
          <path d="M480 280L574 262L668 280L762 244L856 280L950 316L1044 334L1138 388" fill="none" stroke="#5C6170" strokeWidth="1.6" strokeDasharray="5 4" />
          <text x="1150" y="392" fontFamily="Manrope,system-ui,sans-serif" fontSize="12" fontWeight="700" fill="#5C6170">Generic AI</text>
          <g fill="rgba(15,20,32,.12)">
            {[223, 273, 320, 190, 288, 151, 230, 338].map((y, i) => <rect key={i} x={474 + i * 94} y={y} width="12" height="28" rx="6" />)}
          </g>
          <path d="M480 237L574 287L668 334L762 204L856 302L950 165L1044 244L1138 352" fill="none" stroke="#0F1420" strokeWidth="2.4" strokeLinejoin="round" />
          <g fill="#0F1420" stroke="#FAFAF8" strokeWidth="2">
            {[237, 287, 334, 204, 302, 165, 244, 352].map((y, i) => <circle key={i} cx={480 + i * 94} cy={y} r="4.5" />)}
          </g>
          <text x="1150" y="356" fontFamily="Manrope,system-ui,sans-serif" fontSize="13" fontWeight="800" fill="#0F1420">You</text>
        </g>
      </svg>
      <div className="s-ships-fade" />
      <div className="s-w2">
        <div className="s-ships-copy">
          <div className="s-eyebrow">WHY IT’S NOT A WRAPPER</div>
          <h2>Everyone else ships prompts.</h2>
          <p className="s-ships-lead">PersonaDraft ships a behavioral engine — a measurable model of your voice, with a feedback loop that keeps it true.</p>
          <p className="s-ships-body">That’s why it’s <b>consistent</b>, that’s why it <b>compounds</b>, and that’s why it’s <b>not another AI wrapper</b>.</p>
          <a className="s-ulink" href="/how-it-works#act2" onClick={spa('/how-it-works#act2')}>See the engine, step by step →</a>
        </div>
      </div>
    </section>
  );
}

/* ── Trust ──────────────────────────────────────────────────────────────── */

function Trust() {
  return (
    <section className="s-top">
      <div className="s-w2 s-trust2">
        <div><div className="k g">TRUST</div><h3>Insert, never send.</h3><p>PersonaDraft fills the box. You press Send, every time, on every surface.</p></div>
        <div><div className="k">CONTEXT</div><h3>Only what’s on screen.</h3><p>It reads the thread you can see, only when you ask, and shows you exactly what it read.</p></div>
        <div><div className="k">CONSENT</div><h3>Learns only with your yes.</h3><p>It keeps your coordinate — eight numbers — never the text of your drafts.</p></div>
      </div>
    </section>
  );
}

/* ── Teams band (roadmap: everything here is "Coming to Teams") ─────────── */

// Placeholder until a sales route exists.
const TEAMS_MAIL = `mailto:${SUPPORT_EMAIL}?subject=PersonaDraft%20for%20Teams`;

function TeamsBand() {
  const [ref, sc] = useFit(520);
  return (
    <section className="s-teams s-top" id="teams">
      <div className="s-w2">
        <div className="s-teams-card">
          <div className="s-teams-copy">
            <div className="s-teams-k"><span className="s-eyebrow">PERSONADRAFT FOR TEAMS</span><span className="s-pill-o">COMING TO TEAMS</span></div>
            <h2>One voice. Every channel. Every person.</h2>
            <p className="s-teams-sub">PersonaDraft gives your company a consistent, on-brand voice across email, Slack, and chat — so every message sounds like you, whoever sends it.</p>
            <div className="s-outs">
              <span><i />Consistency at scale</span>
              <span><i />Less friction, more velocity</span>
              <span><i />Guardrails, built in</span>
              <span><i />Protection by default</span>
            </div>
            <div className="s-teams-cta"><a className="s-btn inkpill" href={TEAMS_MAIL}>Talk to us about Teams</a></div>
          </div>
          <div className="s-teams-vis" ref={ref}>
            <div className="s-cv tx" style={{ transform: `scale(${sc})` }}>
              <div className="tx-card a">
                <div className="tx-head"><b>Slack</b><span># support-escalations</span></div>
                <div className="s-tline"><PMark size={14} /><span className="rw">Rewritten in <b>Team Voice</b></span><span className="tg">ACME · ON-BRAND</span></div>
                <div className="tx-msg">Thanks for flagging this, Dana. We’ve reproduced it, and the fix ships tonight. I’ll confirm here once it’s live.</div>
              </div>
              <div className="tx-card b">
                <div className="tx-head"><b>Gmail</b><span>To: Northwind procurement</span></div>
                <div className="s-tline"><PMark size={14} /><span className="rw">Rewritten in <b>Team Voice</b></span><span className="tg">GUARDRAIL · PRICING PROMISE REMOVED</span></div>
                <div className="tx-msg">Thanks for the time today. The revised proposal is attached; current pricing holds through March 31.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Page ───────────────────────────────────────────────────────────────── */

export default function Home() {
  // /#teams from another page: scroll once the page has mounted.
  useHashScroll();
  return (
    <div className="pd-site s-v2">
      <SiteNav />

      <header className="s-w2 s-hero2">
        <div className="s-hero2-copy">
          <p className="s-kicker">Most AI guesses how you sound. <b>PersonaDraft knows exactly.</b></p>
          <h1 className="s-h1v">Sound like yourself, everywhere.</h1>
          <p className="s-hero-proof">A behavioral engine that maps your voice, learns it over time, and writes as you — across email, chat, and every social surface.</p>
          <div className="s-ctas">
            <a className="s-btn xl" href={CHROME_STORE_URL} target="_blank" rel="noopener noreferrer">Add to Chrome, free.</a>
            <a className="s-btn ghost lg" href="/how-it-works" onClick={spa('/how-it-works')}>See how it works</a>
          </div>
          <div className="s-heromet">
            <span className="s-ins"><span className="s-gdot" />Insert, never send.</span>
            <span>{HERO_LIVE}{HERO_SOON && <> · <SiteLink href={WAITLIST_PATH}>{HERO_SOON}</SiteLink></>}</span>
          </div>
        </div>
        <HeroLoop />
      </header>

      <FourMoves />
      <Control />
      <EverySurface />
      <ShipsPrompts />
      <Trust />
      <TeamsBand />
      <FinalCta />
      <SiteFooter />
    </div>
  );
}
